/**
 * TravelGroupPanel — shows travel companions and their flight windows for a trip.
 * Lets users create a new group or join an existing one via invite code.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Users, Copy, Check, PlaneLanding, PlaneTakeoff } from "lucide-react";
import { travelGroupApi, type FlightWindow } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import OrganizerRosterPanel from "./OrganizerRosterPanel";

interface Props {
  tripId: string;
  tripName: string;
  /**
   * Show the create / join setup when the trip has no group yet. Off by default:
   * an empty "Travel group: + create / join" row is noise above every trip, so
   * setup is reached from the trip's ⋯ menu instead. A trip that HAS a group
   * always shows it.
   */
  showSetup?: boolean;
}

function WindowBadge({ w }: { w: FlightWindow }) {
  const Icon = w.leg_role === "return" ? PlaneTakeoff : PlaneLanding;
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="font-medium text-foreground">{w.display_name}</span>
      <span>{w.label ?? "flight window pending"}</span>
      <span className="text-muted-foreground/50">({w.departure_date})</span>
    </div>
  );
}

function CopyInviteButton({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  return (
    <button
      onClick={copy}
      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
      aria-label="Copy invite code"
    >
      <span className="font-mono font-bold tracking-widest">{code}</span>
      {copied ? (
        <Check className="h-3.5 w-3.5 text-green-500" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </button>
  );
}

export default function TravelGroupPanel({ tripId, tripName, showSetup = false }: Props) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<null | "create" | "join">(null);
  const [displayName, setDisplayName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [error, setError] = useState("");

  const { data: group, isLoading } = useQuery({
    queryKey: ["travel-group", tripId],
    queryFn: () => travelGroupApi.forTrip(tripId).catch(() => null),
    retry: false,
  });

  const { data: windows } = useQuery({
    queryKey: ["travel-group-windows", group?.id],
    queryFn: () => (group ? travelGroupApi.windows(group.id) : null),
    enabled: !!group,
  });

  const createMut = useMutation({
    mutationFn: () =>
      travelGroupApi.create({
        tripId,
        name: tripName,
        display_name: displayName.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["travel-group", tripId] });
      setMode(null);
      setDisplayName("");
      setError("");
    },
    onError: (e: any) => setError(e.message),
  });

  const joinMut = useMutation({
    mutationFn: () =>
      travelGroupApi.join({
        tripId,
        invite_code: inviteCode.trim(),
        display_name: displayName.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["travel-group", tripId] });
      setMode(null);
      setDisplayName("");
      setInviteCode("");
      setError("");
    },
    onError: (e: any) => setError(e.message),
  });

  if (isLoading) return null;

  // Separate my windows from companions'.
  const otherWindows = (windows ?? []).filter((w) => w.mcp_user_id !== group?.members?.find(
    (m) => m.trip_id === tripId
  )?.mcp_user_id);
  const myWindows = (windows ?? []).filter((w) => w.mcp_user_id === group?.members?.find(
    (m) => m.trip_id === tripId
  )?.mcp_user_id);

  // Group windows by display name for easier reading.
  const byPerson = otherWindows.reduce<Record<string, FlightWindow[]>>((acc, w) => {
    (acc[w.display_name] ??= []).push(w);
    return acc;
  }, {});

  if (!group) {
    if (!showSetup) return null;
    // No group yet — offer create or join.
    return (
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted-foreground font-medium flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" />
          Travel group:
        </span>
        {mode === null && (
          <>
            <button
              onClick={() => { setMode("create"); setError(""); }}
              className="rounded-full border border-dashed border-border bg-background px-2.5 py-0.5 text-xs text-muted-foreground hover:bg-muted transition-colors"
            >
              + create group
            </button>
            <button
              onClick={() => { setMode("join"); setError(""); }}
              className="rounded-full border border-dashed border-border bg-background px-2.5 py-0.5 text-xs text-muted-foreground hover:bg-muted transition-colors"
            >
              join with code
            </button>
          </>
        )}
        {mode === "create" && (
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => { e.preventDefault(); if (displayName.trim()) createMut.mutate(); }}
          >
            <Input
              placeholder="Your name in the group"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="h-7 text-xs w-44"
              autoFocus
            />
            <Button type="submit" size="sm" className="h-7 text-xs" disabled={createMut.isPending || !displayName.trim()}>
              {createMut.isPending ? "Creating…" : "Create"}
            </Button>
            <button type="button" onClick={() => setMode(null)} className="text-xs text-muted-foreground hover:text-foreground">
              cancel
            </button>
            {error && <span className="text-xs text-destructive">{error}</span>}
          </form>
        )}
        {mode === "join" && (
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => { e.preventDefault(); if (displayName.trim() && inviteCode.trim()) joinMut.mutate(); }}
          >
            <Input
              placeholder="Invite code (e.g. A3F2B1C0)"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              className="h-7 text-xs w-36 font-mono"
              autoFocus
            />
            <Input
              placeholder="Your name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="h-7 text-xs w-36"
            />
            <Button type="submit" size="sm" className="h-7 text-xs" disabled={joinMut.isPending || !displayName.trim() || !inviteCode.trim()}>
              {joinMut.isPending ? "Joining…" : "Join"}
            </Button>
            <button type="button" onClick={() => setMode(null)} className="text-xs text-muted-foreground hover:text-foreground">
              cancel
            </button>
            {error && <span className="text-xs text-destructive">{error}</span>}
          </form>
        )}
      </div>
    );
  }

  // Group exists — show companions + windows.
  const otherMembers = group.members.filter((m) => m.trip_id !== tripId);

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="text-muted-foreground font-medium flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" />
          {group.name}
        </span>
        {otherMembers.map((m) => (
          <span
            key={m.mcp_user_id}
            className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-xs font-medium"
          >
            {m.display_name}
          </span>
        ))}
        {otherMembers.length === 0 && (
          <span className="text-xs text-muted-foreground">
            Share invite code to add co-travelers: <CopyInviteButton code={group.invite_code} />
          </span>
        )}
        {otherMembers.length > 0 && (
          <span className="text-xs text-muted-foreground/60">
            invite: <CopyInviteButton code={group.invite_code} />
          </span>
        )}
      </div>

      {/* Flight windows — only when companions have flights set */}
      {Object.keys(byPerson).length > 0 && (
        <div className="flex flex-col gap-0.5 pl-5">
          {Object.entries(byPerson).flatMap(([, ws]) =>
            ws.map((w, i) => <WindowBadge key={i} w={w} />)
          )}
        </div>
      )}

      {/*
        § G4 — the organizer's roster. Rendered unconditionally and gated
        SERVER-side: /roster returns 403 to anyone who is not the group's
        creator, and the panel renders nothing on that. Attendees therefore keep
        exactly the view above (§ G5 — their trip, with the group as context),
        and the authorization lives in one place rather than being re-derived
        from client state that could drift.
      */}
      <OrganizerRosterPanel groupId={group.id} groupName={group.name} inviteCode={group.invite_code} />
    </div>
  );
}
